import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  SelectVendors,
  SelectRawItems,
  SelectHsn,
  SelectUomConversion,
  SelectUnits,
  SelectShapes,
  SelectColors,
  SelectSizes,
  SelectBrandAndSubbrands,
} from "components/ui/selectors";
import Select from "components/ui/Select";
import Input from "components/ui/Input";
import {
  useCreateAgitem,
  useSingleItemByFilter,
} from "http-hooks/agitem/useAgitem";
import useCompanyPreferences from "hooks/useCompanyPreferences";
import useAuth from "hooks/useAuth";
import CombinationsTable from "./CombinationsTable";

const sizeTypeOptions = [
  { value: "B", label: "Big" },
  { value: "S", label: "Small" },
];

export default function SetItemBulk({ onClose }) {
  const [rawItem, setRawItem] = useState(null);
  const [uomConval, setUomConval] = useState("");
  const [isExistingItemCreateFound, setIsExistingItemCreateFound] = useState(false);

  const { agttp } = useAuth();
  const {
    isShapeEnabled,
    isColorEnabled,
    isSizeEnabled,
    isPackRatioEnabled,
    isForeignExportEnabled,
  } =
    useCompanyPreferences();
  const isTaxClassificationOptional = isForeignExportEnabled();

  const [selected, setSelected] = useState({
    itmvendorId: null,
    brandSubbrand: null,
    itmhsnId: null,
    itmastuom: null,
    sizeType: "B",
    quantityPerCase: "",
    shape: [],
    color: [],
    size: [],
  });

  // The pre-built combinations array coming back from CombinationsTable
  const [selectedCombinations, setSelectedCombinations] = useState([]);

  const { mutateAsync: mutateCreate, isPending: isAgitemCreating } = useCreateAgitem();
  const { mutateAsync: checkExistingItem, isPending: isCheckingExisting } =
    useSingleItemByFilter();

  const allFieldsPresent = useMemo(() => {
    const hasVendor = agttp === 0 ? !!selected.itmvendorId : true;
    return hasVendor && !!selected.brandSubbrand && !!rawItem?.item_name;
  }, [agttp, selected.itmvendorId, selected.brandSubbrand, rawItem]);

  const showRemainingFields = allFieldsPresent;

  const prevKeyRef = useRef(null);

  useEffect(() => {
    if (!allFieldsPresent) {
      setIsExistingItemCreateFound(false);
      prevKeyRef.current = null;
      return;
    }

    const currentKey = `${selected.itmvendorId?.ps_Id}-${selected.brandSubbrand?.id}-${rawItem?.item_code}`;
    if (currentKey === prevKeyRef.current) return;
    prevKeyRef.current = currentKey;

    const checkExisting = async () => {
      const checkParams = {
        itemName: rawItem?.item_name,
        itemCode: rawItem?.item_code,
        itmvendorId: selected.itmvendorId?.ps_Id,
        brandSubbrandId: selected.brandSubbrand?.id,
        isSubBrand:
          selected.brandSubbrand?.isSubBrand ||
          selected.brandSubbrand?.level === 2,
      };

      try {
        const existingItem = await checkExistingItem(checkParams);
        if (existingItem) {
          setSelected((prev) => ({
            ...prev,
            itmhsnId: {
              value: existingItem.itmhsnId,
              label: existingItem.itmhsnId,
              hsncode: existingItem.itmhsnId,
            },
            itmastuom: {
              value: existingItem.itmastuom,
              label: existingItem.itmastuom,
              unitname: existingItem.itmastuom,
            },
          }));
          setUomConval({
            itemuom: existingItem.itemuom,
            itembasicuom: existingItem.itmbasicuom,
            conval: existingItem.aguomconval,
            label: `${existingItem.itemuom} = ${existingItem.aguomconval} ${existingItem.itmbasicuom}`,
          });
          setIsExistingItemCreateFound(true);
        } else {
          setIsExistingItemCreateFound(false);
        }
      } catch (error) {
        console.error("Error checking existing item:", error);
        setIsExistingItemCreateFound(false);
      }
    };

    checkExisting();
  }, [allFieldsPresent, selected.itmvendorId, selected.brandSubbrand, rawItem, checkExistingItem]);

  const handleCombinationsChange = useCallback((combinations) => {
    setSelectedCombinations(combinations);
  }, []);

  const handleCreate = async () => {
    try {
      const isSubBrand =
        selected.brandSubbrand?.isSubBrand || selected.brandSubbrand?.level === 2;

      const itemBulk = {
        itmvendorId: selected.itmvendorId?.ps_Id,
        brand: isSubBrand ? null : selected.brandSubbrand,
        subbrand: isSubBrand ? selected.brandSubbrand : null,
        itemName: rawItem?.item_name,
        itemCode: rawItem?.item_code,
        itmhsnId: selected.itmhsnId?.hsncode,
        itemuom: uomConval?.itemuom,
        itmbasicuom: uomConval?.itembasicuom,
        aguomconval: uomConval?.conval,
        itmastuom: selected.itmastuom?.unitname,
        // Send pre-built combinations array; backend just inserts/skips
        combinations: selectedCombinations,
        ...(selected.quantityPerCase !== ""
          ? { quantityPerCase: selected.quantityPerCase }
          : {}),
      };

      await mutateCreate(itemBulk);
      onClose();
    } catch (error) {
      console.error("Create failed:", error);
    }
  };

  const hasVariantSection = isShapeEnabled() || isColorEnabled() || isSizeEnabled();
  const hasCombinationsTable =
    showRemainingFields &&
    (isShapeEnabled() || isColorEnabled() || isSizeEnabled());

  return (
    <div className="min-h-96">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4 mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Add Item (Bulk)</h2>
        <p className="mt-1 text-sm text-gray-500">
          Select a vendor, brand, and item to begin. Additional fields will appear automatically.
        </p>
      </div>

      {/* Step 1: Core Selection */}
      <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-5 mb-6">
        <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide mb-4">
          Item Identification
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SelectVendors
            value={selected.itmvendorId}
            onChange={(vendor) =>
              setSelected((prev) => ({ ...prev, itmvendorId: vendor }))
            }
            placeholder="Select vendor"
            label={agttp === 0 ? "Account/Vendor" : "Account/Vendor (Optional)"}
            isRequired={agttp === 0}
          />

          <SelectBrandAndSubbrands
            value={selected.brandSubbrand}
            onChange={(brandSubbrand) =>
              setSelected((prev) => ({ ...prev, brandSubbrand }))
            }
            label="Brand/Sub-brand"
            placeholder="Select brand or sub-brand"
            isRequired={true}
            filters={{ ag_status: "1" }}
          />

          <SelectRawItems
            value={rawItem}
            onChange={(selectedOption) => setRawItem(selectedOption || null)}
            placeholder="Search items..."
            isRequired={true}
          />
        </div>
      </div>

      {isCheckingExisting && (
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
          <svg
            className="animate-spin h-4 w-4 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          Checking for existing item...
        </div>
      )}

      {/* Step 2: Details + Variants */}
      {showRemainingFields && (
        <div
          className={`grid grid-cols-1 ${hasVariantSection ? "lg:grid-cols-5" : ""} gap-6`}
        >
          {/* Left: Item Details */}
          <div
            className={`${hasVariantSection ? "lg:col-span-3" : ""} rounded-lg border border-gray-200 bg-white p-5`}
          >
            <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide mb-4">
              Item Details
            </h3>

            {isExistingItemCreateFound && (
              <div className="mb-4 rounded-md bg-blue-50 border border-blue-200 px-4 py-2.5">
                <p className="text-sm text-blue-700">
                  Existing item found — HSN, UOM, and Unit have been auto-filled.
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectHsn
                value={selected.itmhsnId}
                onChange={(selectedOption) =>
                  setSelected((prev) => ({ ...prev, itmhsnId: selectedOption }))
                }
                label="HSN / SAC"
                placeholder="Search HSN or SAC..."
                isRequired={!isTaxClassificationOptional}
                isDisabled={isExistingItemCreateFound}
              />

              <SelectUomConversion
                value={uomConval}
                onChange={(selectedOption) =>
                  setUomConval(selectedOption || {})
                }
                placeholder="Search conversion..."
                isRequired={true}
                isDisabled={isExistingItemCreateFound}
              />

              <SelectUnits
                value={selected.itmastuom}
                onChange={(selectedOption) =>
                  setSelected((prev) => ({ ...prev, itmastuom: selectedOption }))
                }
                unitidunder={isSizeEnabled() ? 1 : 4}
                placeholder="Search unit of measurement..."
                isRequired={isSizeEnabled()}
                isDisabled={isExistingItemCreateFound}
              />

              {isPackRatioEnabled() && (
                <Input
                  label="Quantity in each case"
                  value={selected.quantityPerCase}
                  onChange={(e) =>
                    setSelected((prev) => ({
                      ...prev,
                      quantityPerCase: e.target.value,
                    }))
                  }
                  placeholder="Quantity in each case"
                  type="number"
                  isRequired={true}
                />
              )}
            </div>
          </div>

          {/* Right: Attributes (multi-select to seed the combinations table) */}
          {hasVariantSection && (
            <div className="lg:col-span-2 rounded-lg border border-gray-200 bg-white p-5">
              <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide mb-4">
                Attributes
              </h3>
              <div className="space-y-4">
                {isShapeEnabled() && (
                  <SelectShapes
                    value={selected.shape}
                    onChange={(selectedOptions) =>
                      setSelected((prev) => ({
                        ...prev,
                        shape: selectedOptions || [],
                      }))
                    }
                    placeholder="Search shapes..."
                    isMulti={true}
                    isRequired={true}
                  />
                )}

                {isColorEnabled() && (
                  <SelectColors
                    value={selected.color}
                    onChange={(selectedOptions) =>
                      setSelected((prev) => ({
                        ...prev,
                        color: selectedOptions || [],
                      }))
                    }
                    placeholder="Search colors..."
                    isMulti={true}
                    isRequired={true}
                  />
                )}

                {isSizeEnabled() && (
                  <SelectSizes
                    value={selected.size}
                    onChange={(selectedOptions) =>
                      setSelected((prev) => ({
                        ...prev,
                        size: selectedOptions || [],
                      }))
                    }
                    filters={{
                      umid: selected?.itmastuom?.unitname,
                    }}
                    placeholder="Search sizes..."
                    isMulti={true}
                    isRequired={true}
                  />
                )}

                {isSizeEnabled() && (
                  <Select
                    label="Item Size Type"
                    value={sizeTypeOptions.find(
                      (option) => option.value === selected.sizeType
                    )}
                    onChange={(selectedOption) =>
                      setSelected((prev) => ({
                        ...prev,
                        sizeType: selectedOption?.value || "B",
                      }))
                    }
                    options={sizeTypeOptions}
                    placeholder="Select size type"
                    isSearchable={false}
                    isClearable={false}
                    isRequired={true}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Combinations preview table */}
      {hasCombinationsTable && (
        <CombinationsTable
          shapes={selected.shape}
          colors={selected.color}
          sizes={selected.size}
          sizeType={selected.sizeType}
          unitName={selected.itmastuom?.unitname}
          isShapeEnabled={isShapeEnabled()}
          isColorEnabled={isColorEnabled()}
          isSizeEnabled={isSizeEnabled()}
          onChange={handleCombinationsChange}
        />
      )}

      {/* Footer */}
      <div className="mt-8 flex items-center justify-end gap-x-3 border-t border-gray-200 pt-5">
        <button
          type="button"
          className="rounded-md px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 shadow-sm hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400 disabled:opacity-50"
          onClick={onClose}
          disabled={isAgitemCreating}
        >
          Cancel
        </button>
        {showRemainingFields && (
          <button
            type="button"
            disabled={
              isAgitemCreating ||
              (hasCombinationsTable && selectedCombinations.length === 0)
            }
            onClick={handleCreate}
            className="rounded-md bg-gray-800 px-5 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isAgitemCreating
              ? "Saving..."
              : hasCombinationsTable
              ? `Create ${selectedCombinations.length} Item${selectedCombinations.length !== 1 ? "s" : ""}`
              : "Create Items"}
          </button>
        )}
      </div>
    </div>
  );
}
